import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { buildPath } from "@/lib/routes";
import { useLang } from "@/hooks/useLang";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/motion/Reveal";
import { breadcrumbSchema } from "@/lib/schema";

const C = {
  da: {
    title: "Sitemap",
    heading: "Alle sider på magnusloev.dk",
    lead: "Et fuldt overblik over sider, ydelser, projekter, tech stacks, artikler og værktøjer på magnusloev.dk. God gammeldags navigation for både mennesker og AI-crawlers.",
    seoTitle: "Sitemap | Magnus Løv Schmidt",
    seoDesc: "HTML-sitemap for magnusloev.dk. Overblik over alle sider — ydelser, projekter, tech stacks, artikler og værktøjer.",
    core: "Kernenavigation",
    services: "Ydelser",
    projects: "Projekter",
    techstacks: "Tech Stacks",
    blog: "Viden & artikler",
    tools: "Værktøjer (affiliate)",
    xmlNote: "Kigger du efter XML-sitemap til søgemaskiner?",
    xmlCta: "Gå til XML-sitemap",
    home: "Forside",
    about: "Om mig",
    contact: "Kontakt",
    servicesHub: "Alle ydelser",
    projectsHub: "Alle projekter",
    stacksHub: "Alle tech stacks",
    blogHub: "Alle artikler",
  },
  en: {
    title: "Sitemap",
    heading: "All pages on magnusloev.dk",
    lead: "A full overview of pages, services, projects, tech stacks, articles and tools on magnusloev.dk. Good old-fashioned navigation for humans and AI crawlers alike.",
    seoTitle: "Sitemap | Magnus Løv Schmidt",
    seoDesc: "HTML sitemap for magnusloev.dk. Overview of all pages — services, projects, tech stacks, articles and tools.",
    core: "Core navigation",
    services: "Services",
    projects: "Projects",
    techstacks: "Tech Stacks",
    blog: "Blog & articles",
    tools: "Tools (affiliate)",
    xmlNote: "Looking for the XML sitemap for search engines?",
    xmlCta: "Go to XML sitemap",
    home: "Home",
    about: "About",
    contact: "Contact",
    servicesHub: "All services",
    projectsHub: "All projects",
    stacksHub: "All tech stacks",
    blogHub: "All articles",
  },
};

function Group({ heading, children, testid }) {
  return (
    <Reveal>
      <section data-testid={testid} className="border-t border-border py-10">
        <div className="label-mono mb-6 text-muted-foreground/60">{heading}</div>
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">{children}</ul>
      </section>
    </Reveal>
  );
}

function Item({ to, label, sub, testid, external }) {
  const inner = (
    <div className="flex items-start justify-between gap-3 rounded-md border border-transparent px-3 py-2.5 transition-colors hover:border-border hover:bg-secondary/40">
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">{label}</div>
        {sub && <div className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground/70">{sub}</div>}
      </div>
      <ArrowUpRight size={14} className="mt-1 shrink-0 text-muted-foreground" />
    </div>
  );
  return (
    <li>
      {external ? (
        <a href={to} target="_blank" rel="noopener noreferrer" data-testid={testid}>{inner}</a>
      ) : (
        <Link to={to} data-testid={testid}>{inner}</Link>
      )}
    </li>
  );
}

export default function SitemapPage() {
  const { lang } = useLang();
  const c = C[lang];

  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: api.getServices });
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: api.getProjects });
  const { data: stacks = [] } = useQuery({ queryKey: ["stacks"], queryFn: api.getTechStacks });
  const { data: posts = [] } = useQuery({ queryKey: ["blog"], queryFn: api.getBlog });

  const jsonLd = [
    breadcrumbSchema([
      { name: c.home, path: buildPath("home", lang) },
      { name: c.title, path: buildPath("sitemap", lang) },
    ]),
  ];

  return (
    <>
      <Seo routeKey="sitemap" lang={lang} title={c.seoTitle} description={c.seoDesc} jsonLd={jsonLd} />

      <section className="border-b border-border px-6 md:px-12 lg:px-20 pt-16 pb-10 md:pt-20">
        <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] max-w-4xl">
          {c.heading}
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-muted-foreground font-light leading-relaxed">{c.lead}</p>
        <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
          <span className="text-muted-foreground">{c.xmlNote}</span>
          <a href="/sitemap.xml" data-testid="sitemap-xml-link"
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 font-mono text-xs uppercase tracking-wide transition-colors hover:border-primary hover:text-primary">
            {c.xmlCta}
            <ArrowUpRight size={12} weight="bold" />
          </a>
        </div>
      </section>

      <div className="px-6 md:px-12 lg:px-20 pb-24">
        <Group heading={c.core} testid="sitemap-group-core">
          <Item to={buildPath("home", lang)} label={c.home} sub={buildPath("home", lang)} testid="sitemap-link-home" />
          <Item to={buildPath("about", lang)} label={c.about} sub={buildPath("about", lang)} testid="sitemap-link-about" />
          <Item to={buildPath("contact", lang)} label={c.contact} sub={buildPath("contact", lang)} testid="sitemap-link-contact" />
          <Item to={buildPath("services", lang)} label={c.servicesHub} sub={buildPath("services", lang)} testid="sitemap-link-services-hub" />
          <Item to={buildPath("projects", lang)} label={c.projectsHub} sub={buildPath("projects", lang)} testid="sitemap-link-projects-hub" />
          <Item to={buildPath("techstacks", lang)} label={c.stacksHub} sub={buildPath("techstacks", lang)} testid="sitemap-link-stacks-hub" />
          <Item to={buildPath("blog", lang)} label={c.blogHub} sub={buildPath("blog", lang)} testid="sitemap-link-blog-hub" />
        </Group>

        {services.length > 0 && (
          <Group heading={c.services} testid="sitemap-group-services">
            {services.map((s) => {
              const cc = s[lang] || s.da;
              const path = buildPath("service", lang, { slug: s.slug });
              return <Item key={s.slug} to={path} label={cc.title} sub={path} testid={`sitemap-link-service-${s.slug}`} />;
            })}
          </Group>
        )}

        {projects.length > 0 && (
          <Group heading={c.projects} testid="sitemap-group-projects">
            {projects.map((p) => {
              const cc = p[lang] || p.da;
              if (p.external_only) {
                return <Item key={p.slug} to={p.external_url} label={cc.title} sub={p.external_url} testid={`sitemap-link-project-${p.slug}`} external />;
              }
              const path = buildPath("project", lang, { slug: p.slug });
              return <Item key={p.slug} to={path} label={cc.title} sub={path} testid={`sitemap-link-project-${p.slug}`} />;
            })}
          </Group>
        )}

        {stacks.length > 0 && (
          <Group heading={c.techstacks} testid="sitemap-group-stacks">
            {stacks.map((s) => {
              const cc = s[lang] || s.da;
              const path = buildPath("techstack", lang, { slug: s.slug });
              return <Item key={s.slug} to={path} label={cc.title} sub={path} testid={`sitemap-link-stack-${s.slug}`} />;
            })}
          </Group>
        )}

        {stacks.some((s) => s.tools?.length) && (
          <Group heading={c.tools} testid="sitemap-group-tools">
            {stacks.flatMap((s) =>
              (s.tools || []).map((tool) => {
                const path = buildPath("tool", lang, { stack: s.slug, tool: tool.slug });
                return (
                  <Item
                    key={`${s.slug}-${tool.slug}`}
                    to={path}
                    label={tool.name}
                    sub={`${s[lang]?.title || s.da.title} · ${path}`}
                    testid={`sitemap-link-tool-${s.slug}-${tool.slug}`}
                  />
                );
              })
            )}
          </Group>
        )}

        {posts.length > 0 && (
          <Group heading={c.blog} testid="sitemap-group-blog">
            {posts.map((p) => {
              const cc = p[lang] || p.da;
              const path = buildPath("post", lang, { slug: p.slug });
              return <Item key={p.slug} to={path} label={cc.title} sub={path} testid={`sitemap-link-post-${p.slug}`} />;
            })}
          </Group>
        )}
      </div>
    </>
  );
}
